'use strict';
import { Tooltip } from '@mui/material'; // Add this import at the top with other imports
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material'; // Add to your imports
import TextField from '@mui/material/TextField'; // Add this import
import dayjs from 'dayjs'; // Add this import at the top if not already present
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);


import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { projectFilesComponentSelector } from '@/redux/selectors';
import { Button, DynamicModal, DynamicModalButton, EmptyState, Icon, Toaster } from '@/components/index.jsx';
import FilesTable from '@/containers/FilesTable.jsx';
import SearchContainer from '@/containers/SearchContainer.jsx';
import './ProjectFiles.scss';
import RefreshIndicator from '@/components/controls/RefreshIndicator.jsx';
import * as enums from '@/enums/Enums';
import { useLocalAuditLogger } from '@/hooks/useLocalAuditLogger';
import { useLogger, Log } from '@/utils/Logger';
import { AppInsightLogLevel } from '@/enums/Enums';
import { toast } from 'react-toastify';
import { LoadingSpinner } from 'metabux/components/LoadingSpinner/LoadingSpinner';
import AttachmentUploader from './AttachmentUploader.jsx';
import { getFilesMissingProjectCode, getMissingProjectCodeMessages } from './projectCodeFileGuard';
import {
	filesFetchData as filesFetchDataActionCreator,
	filesFetchDataAdmin as filesFetchDataAdminActionCreator,
	fetchAllFiles as fetchAllFilesActionCreator,
	AdminFetchAllFiles as adminFetchAllFilesActionCreator,
	fetchFilesByIds as fetchFilesByIdsActionCreator,
	AdminFetchFilesByIds as adminFetchFilesByIdsActionCreator,
	filesPublishAdmin as filesPublishAdminActionCreator,
	fileRemoveAdmin as fileRemoveAdminActionCreator,
	updateFilePropertiesAdmin as updateFilePropertiesAdminActionCreator,
	setRecognizeRevenue as recognizeRevenueActionCreator,
	getRecognizeRevenue as getRecognizeRevenueActionCreator
} from '@/redux/actions/files';
import { usePermissions } from '@/hooks/permissions.hook';
import * as permissions from '@/security/permissionTypes';
import {
	Box,
	Stack,
	FormControlLabel,
	Switch,
	Typography,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
} from '@mui/material';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { LAB_VANTAGE_EDITED_TOOLTIP } from '@/constants/labVantageUi';
import { ENABLE_LAB_VANTAGE_IMPORT } from '@/config/env.config';
import { isLabVantageCoreProject } from '@/utils/isLabVantageCoreProject';
import { emitPortalResultsDownload } from '@/usageInsights/usageInsights';
import {
	useAdminLabVantageImportMutation,
	useAdminLabVantageNoticeAcknowledgeMutation,
	useAdminLabVantageSummaryQuery,
} from '@/redux/services/portal/files.api';

export const getMismatchedPublishFileNames = (files = [], projectCode = '') =>
	getFilesMissingProjectCode(files, projectCode);

export const ProjectFiles = (props) => {
	const timeoutRef = useRef(null);
	const logger = useLogger();
	const auditLogger = useLocalAuditLogger();
	const { checkUserHasRole, checkUserHasPermissions } = usePermissions();
	const data = useSelector(projectFilesComponentSelector);
	const [project, setProject] = useState(props.project);
	const [allFiles, setAllFiles] = useState([]);
	const [visibleFiles, setVisibleFiles] = useState(data?.files || []);
	const [selectedFileIds, setSelectedFileIds] = useState([]);
	const [selectedStagedFiles, setSelectedStagedFiles] = useState([]);
	const [showFiles, setShowFiles] = useState(false);
	const [showSearchDialog, setShowSearchDialog] = useState(false);
	const [fileChanges, setFileChanges] = useState([]);
	const [isSavingFiles, setIsSavingFiles] = useState(false);
	const [publishValidationDialogOpen, setPublishValidationDialogOpen] = useState(false);
	const [publishValidationMessages, setPublishValidationMessages] = useState([]);

	// Callback to update a specific file's data without refreshing all files
	const updateFileInVisibleFiles = useCallback((updatedFile) => {
		setVisibleFiles(prevFiles => 
			prevFiles.map(file => 
				file.fileId === updatedFile.fileId ? { ...file, ...updatedFile } : file
			)
		);
	}, []);
	const [errormsg, setErrormsg] = useState('');
	const [attachmentIsLoading, setAttachmentIsLoading] = useState(false);
	const [useAdminEndpoint, setUseAdminEndpoint] = useState(false);
	const dispatch = useDispatch();

	const fetchData = (params) => dispatch(filesFetchDataActionCreator(params));
	const fetchDataAdmin = (params) => dispatch(filesFetchDataAdminActionCreator(params));
	const filesPublishAdmin = (params) => dispatch(filesPublishAdminActionCreator(params));
	const fileRemoveAdmin = (params) => dispatch(fileRemoveAdminActionCreator(params));
	const updateFilePropertiesAdmin = (params) => dispatch(updateFilePropertiesAdminActionCreator(params));
	const fetchAllFilesAction = (project) => dispatch(fetchAllFilesActionCreator(project));
	const adminFetchAllFilesAction = (project) => dispatch(adminFetchAllFilesActionCreator(project));
	const fetchFilesByIds = (project, fileIds) => dispatch(fetchFilesByIdsActionCreator(project, fileIds));
	const adminFetchFilesByIds = (project, fileIds) => dispatch(adminFetchFilesByIdsActionCreator(project, fileIds));
	const recognizeRevenue = (params) => dispatch(recognizeRevenueActionCreator(params));
	const getRecognizeRevenue = (projectId) => dispatch(getRecognizeRevenueActionCreator(projectId));
	// Pipeline status is informational only: the Publish action relies solely on the
	// authoritative data.isPublishing state (approved decoupling decision).
	const [revenueComment, setRevenueComment] = useState({
		Comment: '',
		UserId: null,
		Date: new Date().toISOString()
		
	}
	);
	const [recognizeComponentData, setRecognizeComponentData] = useState({
		ButtonText: 'Recognize Revenue',
		TooltipText: '',
	});


	/**
 * @typedef {Object} RecognizeRevenuePayload
 * @property {string} RecognizeRevenueId
 * @property {string} RecognizeRevenueDate
 * @property {string} UserId
 * @property {string} ProjectId
 * @property {Object} Comment
 * @property {string} Status
 */


	// Add this useState near the top of your component
	const [recognizeRevenuePayload, setRecognizeRevenuePayload] = useState({
		RecognizeRevenueDate: new Date().toISOString(),
		ProjectId: project?.projectId || null,
		Status: 'Pending',
		Comment: {
			Comment: '',
			CreatedDate: new Date().toISOString()
		}
	});




	const isAdmin = useMemo(() => checkUserHasRole([enums.ROLES.DTS, enums.ROLES.ADMIN]), []);

	const isCorePipelineProject = useMemo(
		() => isLabVantageCoreProject(project?.pipeline),
		[project?.pipeline],
	);
	const labVantageImportEnabled = ENABLE_LAB_VANTAGE_IMPORT && isCorePipelineProject;
	const shouldFetchLabVantageSummary =
		labVantageImportEnabled &&
		Boolean(project?.projectId) &&
		isAdmin &&
		useAdminEndpoint;

	const {
		data: labVantageSummary,
		isLoading: labVantageSummaryLoading,
		refetch: refetchLabVantageSummaryQuery,
	} = useAdminLabVantageSummaryQuery(
		shouldFetchLabVantageSummary && project?.projectId ? String(project.projectId) : skipToken
	);

	const [labVantageImportMutation, { isLoading: labVantageImportSubmitting }] =
		useAdminLabVantageImportMutation();
	const [labVantageNoticeAcknowledgeMutation, { isLoading: labVantageAcknowledgeSubmitting }] =
		useAdminLabVantageNoticeAcknowledgeMutation();

	const canEditProjects = useMemo(() => {
		try {
			return checkUserHasPermissions(permissions.CAN_EDIT_PROJECTS);
		} catch (error) {
			console.warn('Error checking canEditProjects permission:', error);
			return false;
		}
	}, [checkUserHasPermissions]);

	const loadAdminData = useCallback(async (projectId) => {
		if (!projectId) return;
		const files = await fetchDataAdmin(projectId);
		// Admin endpoint shows all files (both customer visible and not, staged and published)
		const filesToShow = files?.response || [];
		setVisibleFiles(filesToShow);
		setAllFiles(filesToShow);
	}, []);

	const loadRegularData = useCallback(async (projectId) => {
		if (!projectId) return;
		const files = await fetchData(projectId);
		// Regular endpoint shows only published, customer-visible files
		const publishedFiles = files?.response ? files.response.filter((e) => e.staged == false) : [];
		setVisibleFiles(publishedFiles);
		setAllFiles(publishedFiles);
	}, []);

	/** Server-derived notice cohort count (uses lastAcknowledgedUtc); 0 until summary loads or when fully acknowledged. */
	const labVantageNoticeBadgeCount = useMemo(() => {
		const n = Number(labVantageSummary?.noticeCount);
		return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
	}, [labVantageSummary]);

	const [labVantageUploadModalOpen, setLabVantageUploadModalOpen] = useState(false);
	const [labVantageImportSelection, setLabVantageImportSelection] = useState([]);

	useEffect(() => {
		if (!shouldFetchLabVantageSummary) {
			setLabVantageUploadModalOpen(false);
			setLabVantageImportSelection([]);
		}
	}, [shouldFetchLabVantageSummary]);

	const closeLabVantageUploadModal = useCallback(() => {
		setLabVantageUploadModalOpen(false);
		setLabVantageImportSelection([]);
	}, []);

	const labVantageAttachments = useMemo(
		() => (Array.isArray(labVantageSummary?.attachments) ? labVantageSummary.attachments : []),
		[labVantageSummary]
	);

	const labVantageEditedPortalFileIds = useMemo(() => {
		const attachments = labVantageSummary?.attachments;
		if (!Array.isArray(attachments)) return new Set();
		const next = new Set();
		for (const row of attachments) {
			const classificationNorm = String(row?.classification || '').toLowerCase();
			if (classificationNorm !== 'edited') continue;
			const portalId = row?.correlatedPortalFileId;
			if (portalId != null && portalId !== '')
				next.add(String(portalId).trim().toLowerCase());
		}
		return next;
	}, [labVantageSummary]);

	/** Optional portalFileId: open dialog with matching edited LV row(s) pre-checked (files table icon). */
	const openLabVantageUploadModal = useCallback(
		(portalFileId) => {
			const raw =
				portalFileId != null &&
				portalFileId !== '' &&
				typeof portalFileId !== 'object'
					? portalFileId
					: null;
			const normalizedPortal = raw != null ? String(raw).trim().toLowerCase() : null;

			if (normalizedPortal == null) {
				setLabVantageImportSelection([]);
			} else {
				const attachments = labVantageSummary?.attachments;
				const lvIds = [];
				if (Array.isArray(attachments)) {
					for (const row of attachments) {
						const classificationNorm = String(row?.classification || '').toLowerCase();
						if (classificationNorm !== 'edited') continue;
						const corr = row?.correlatedPortalFileId;
						if (corr == null || corr === '') continue;
						if (String(corr).trim().toLowerCase() !== normalizedPortal) continue;
						const lv = row?.lvFileId;
						if (lv) lvIds.push(lv);
					}
				}
				setLabVantageImportSelection(lvIds);
			}
			setLabVantageUploadModalOpen(true);
		},
		[labVantageSummary]
	);

	const toggleLabVantageImportSelection = useCallback((lvFileId, classificationNorm) => {
		if (classificationNorm === 'current') return;
		setLabVantageImportSelection((prev) => {
			const next = new Set(prev);
			if (next.has(lvFileId)) next.delete(lvFileId);
			else next.add(lvFileId);
			return [...next];
		});
	}, []);

	const formatLabVantageModifiedAt = useCallback((value) => {
		if (value == null || value === '') return '--';
		const d = dayjs(value);
		return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '--';
	}, []);

	const refreshLabVantageSummaryIntoState = useCallback(async () => {
		if (!project?.projectId || !shouldFetchLabVantageSummary) return;
		try {
			await refetchLabVantageSummaryQuery().unwrap();
		} catch {
			// RTK keeps last successful data on failed refetch
		}
	}, [project?.projectId, shouldFetchLabVantageSummary, refetchLabVantageSummaryQuery]);

	const refreshFilesWithMode = useCallback(async (adminMode) => {
		if (!project.projectId) { return; }
		if (project.projectId) {
			if (adminMode) {
				// Use admin endpoint when admin mode is selected
				await loadAdminData(project.projectId);
			} else {
				// Use regular endpoint when regular mode is selected
				await loadRegularData(project.projectId);
			}

			localStorage.setItem("CurrentFilesProjectId", project.projectId);
			localStorage.setItem("CurrentProjectPublishDate", project.publishDate);
		}
		setShowFiles(true);

		return () => {
			localStorage.removeItem("CurrentFilesProjectId");
			localStorage.removeItem("CurrentProjectPublishDate");
		};
	}, [project.projectId, project.publishDate, loadAdminData, loadRegularData]);

	const refreshFiles = useCallback(async () => {
		if (!project.projectId) { return; }
		if (project.projectId) {
			if (useAdminEndpoint) {
				// Use admin endpoint when admin mode is selected
				await loadAdminData(project.projectId);
			} else {
				// Use regular endpoint when regular mode is selected
				await loadRegularData(project.projectId);
			}

			localStorage.setItem("CurrentFilesProjectId", project.projectId);
			localStorage.setItem("CurrentProjectPublishDate", project.publishDate);
		}
		setShowFiles(true);

		return () => {
			localStorage.removeItem("CurrentFilesProjectId");
			localStorage.removeItem("CurrentProjectPublishDate");
		};
	}, [useAdminEndpoint, project.projectId, project.publishDate, loadAdminData, loadRegularData]);

	const onRemoveFile = useCallback(async (file) => {
		await fileRemoveAdmin(file)
		refreshFiles()
	}, [fileRemoveAdmin, refreshFiles]);

	const handleToggleChange = useCallback((event) => {
		const newUseAdminEndpoint = event.target.checked;
		setUseAdminEndpoint(newUseAdminEndpoint);
		// Refresh files when endpoint changes with the new value
		refreshFilesWithMode(newUseAdminEndpoint);
	}, []);


	useEffect(() => {
		// Set initial admin mode based on permissions and refresh files
		setUseAdminEndpoint(canEditProjects);
		refreshFilesWithMode(canEditProjects);
	}, [canEditProjects]);

	useEffect(() => {
		setProject(props.project);
	}, [props.project]);

	const onSearchChange = useCallback((text) => {
		if (text.length == 0) {
			setVisibleFiles(allFiles);
		}

		if (allFiles) {
			const searchTerm = text.toLowerCase();
			const fileList = allFiles.filter((file) => file.name.toLowerCase().includes(searchTerm));

			setVisibleFiles(fileList);
		}
	}, [allFiles]);

	const keyUpHandler = useCallback((e) => {
		clearTimeout(timeoutRef.current);

		let text = '';

		if (e.target) {
			text = e.target.value;
		}

		if (data?.files && data.files.length > 1000) {
			timeoutRef.current = setTimeout(() => {
				onSearchChange(text);
			}, 800);
		} else {
			onSearchChange(text);
		}
	}, [allFiles]);

	const checkboxClickHandler = useCallback((selectedFiles) => {
		setSelectedFileIds(selectedFiles);
	}, []);

	// Keep selectedStagedFiles in sync with selectedFileIds and allFiles
	useEffect(() => {
		const stagedSelections = allFiles.filter(f => selectedFileIds.includes(f.fileId) && f.staged);
		setSelectedStagedFiles(stagedSelections);
	}, [selectedFileIds, allFiles]);

	const auditLogFileDownload = (projectId, listOfFileNames) => {
		auditLogger.logProjectFileDownload(projectId, listOfFileNames);
	};

	const publishFiles = useCallback(async () => {
		try {
			console.log('Starting file publish...', selectedStagedFiles.length, 'files');
			const publishResult = await filesPublishAdmin(selectedStagedFiles);
			console.log('Publish completed:', publishResult);
			
			// Clear the selected files first
			setSelectedFileIds([]);
			setSelectedStagedFiles([]);
			
			// Add a small delay to ensure state updates are processed
			await new Promise(resolve => setTimeout(resolve, 100));
			
			// Refresh the files list after successful publishing
			console.log('Refreshing files...');
			await refreshFiles();
			console.log('Files refreshed successfully');

			//const allFileNames = selectedStagedFiles.map((f) => f.name);
			//auditLogFileDownload(project.projectId, allFileNames);

			const logPublish = new Log();
			const allFileSizes = selectedStagedFiles.map((f) => f.size);
			logPublish.SetLevel(AppInsightLogLevel.EVENT);
			logPublish.SetName('PageEvent_FilePublish');
			logPublish.AddProperty('Filesize', allFileSizes);
			logPublish.AddProperty("PublishDate", project.publishDate);
			logPublish.AddProperty('Count', selectedStagedFiles.length);
			logPublish.AddProperty('ProjectId', project.projectId);

			logger.doLog(logPublish);
		} catch (error) {
			console.error('Error publishing files:', error);
			showToaster('Publish Error', 'Failed to publish files. Please try again.');
		}
	}, [selectedStagedFiles, refreshFiles, project.projectId, project.publishDate, logger]);

	const handlePublishClick = useCallback(() => {
		const mismatchedFileNames = getMismatchedPublishFileNames(selectedStagedFiles, project?.projectCode);

		if (mismatchedFileNames.length > 0) {
			setPublishValidationMessages(getMissingProjectCodeMessages(selectedStagedFiles, project?.projectCode));
			setPublishValidationDialogOpen(true);
			return;
		}

		publishFiles();
	}, [selectedStagedFiles, project?.projectCode, publishFiles]);

	const closePublishValidationDialog = useCallback(() => {
		setPublishValidationDialogOpen(false);
		setPublishValidationMessages([]);
	}, []);

	const confirmPublishWithMismatch = useCallback(() => {
		setPublishValidationDialogOpen(false);
		publishFiles();
	}, [publishFiles]);

	const fetchAllFiles = useCallback(() => {
		if (useAdminEndpoint) {
			adminFetchAllFilesAction(project);
		} else {
			fetchAllFilesAction(project);
		}

		const allFileNames = data?.files ? data.files.map((f) => f.name) : [];
		const allFileSizes = data?.files ? data.files.map((f) => f.size) : [];

		auditLogFileDownload(project.projectId, allFileNames);
		emitPortalResultsDownload(project);

		const logDownload = new Log();

		logDownload.SetLevel(AppInsightLogLevel.EVENT);
		logDownload.SetName('PageEvent_FileDownload_All');
		logDownload.AddProperty('Filesize', allFileSizes);
		logDownload.AddProperty("PublishDate", project.publishDate);
		logDownload.AddProperty('Count', allFileNames.length);
		logDownload.AddProperty('ProjectId', project.projectId);

		logger.doLog(logDownload);
	}, [project, useAdminEndpoint]);

	const fetchFilesById = useCallback(() => {
		if (useAdminEndpoint) {
			adminFetchFilesByIds(project, selectedFileIds);
		} else {
			fetchFilesByIds(project, selectedFileIds);
		}

		const filteredFiles = data?.files ? data.files.filter((f) => selectedFileIds.includes(f.fileId)) : [];
		const filteredfileNames = filteredFiles.map((f) => f.name);
		const filteredFileSizes = filteredFiles.map((f) => f.size);

		auditLogFileDownload(project.projectId, filteredfileNames);
		emitPortalResultsDownload(project);

		const logDownload = new Log();

		logDownload.SetLevel(AppInsightLogLevel.EVENT);
		logDownload.SetName('PageEvent_FileDownload_Selected');
		logDownload.AddProperty('Filesize', filteredFileSizes);
		logDownload.AddProperty("PublishDate", project.publishDate);
		logDownload.AddProperty('Count', filteredFileSizes.length);
		logDownload.AddProperty('ProjectId', project.projectId);

		logger.doLog(logDownload);
	}, [project, selectedFileIds, useAdminEndpoint]);

	const showToaster = (title, body) => {
		toast(<Toaster title={title} body={body} />);
	};

	const clearLabVantageNotice = useCallback(async () => {
		const projectId = project?.projectId;
		if (!projectId) return;
		try {
			await labVantageNoticeAcknowledgeMutation({ projectId }).unwrap();
			showToaster('Lab Vantage', 'Lab Vantage notice cleared.');
			closeLabVantageUploadModal();
			await refreshLabVantageSummaryIntoState();
		} catch (err) {
			const detail =
				err?.data?.detail ||
				err?.data?.title ||
				err?.message ||
				'Could not clear Lab Vantage notice.';
			showToaster('Lab Vantage', String(detail));
		}
	}, [
		project?.projectId,
		labVantageNoticeAcknowledgeMutation,
		closeLabVantageUploadModal,
		refreshLabVantageSummaryIntoState,
	]);

	const submitLabVantageImport = useCallback(async () => {
		const lvFileIds = labVantageImportSelection.filter(Boolean);
		if (lvFileIds.length === 0) {
			showToaster('Lab Vantage import', 'Select at least one file to import.');
			return;
		}
		const projectId = project?.projectId;
		if (!projectId) return;

		try {
			const response = await labVantageImportMutation({
				projectId,
				lvFileIds,
				acknowledgeNotice: true,
			}).unwrap();

			const results = Array.isArray(response?.results) ? response.results : [];
			const ok = results.filter((r) => r?.success === true).length;
			const fail = results.filter((r) => r?.success === false).length;
			let detail =
				results.length === 0
					? 'Import finished.'
					: `${ok} imported successfully`;
			if (results.length > 0 && fail > 0) detail += `, ${fail} failed`;
			showToaster('Lab Vantage import', detail);

			closeLabVantageUploadModal();
			await refreshFiles();
			await refreshLabVantageSummaryIntoState();
		} catch (err) {
			const payload = err?.data;
			const resultRows = Array.isArray(payload?.results) ? payload.results : [];
			if (resultRows.length > 0) {
				const ok = resultRows.filter((r) => r?.success === true).length;
				const fail = resultRows.filter((r) => r?.success === false).length;
				let detail =
					ok === 0
						? `${fail} failed`
						: `${ok} imported successfully${fail > 0 ? `, ${fail} failed` : ''}`;
				showToaster('Lab Vantage import', detail);
				if (ok > 0) {
					await refreshFiles();
					await refreshLabVantageSummaryIntoState();
				}
				return;
			}
			const detail =
				payload?.detail ||
				payload?.title ||
				err?.data?.detail ||
				err?.data?.title ||
				err?.message ||
				'Import failed.';
			showToaster('Lab Vantage import', String(detail));
		}
	}, [
		labVantageImportSelection,
		project?.projectId,
		labVantageImportMutation,
		closeLabVantageUploadModal,
		refreshFiles,
		refreshLabVantageSummaryIntoState,
	]);


	const updateFileChanges = useCallback((changes) => {
		setFileChanges(changes);
		setErrormsg('');
	}, []);

	const loadingStyle = useMemo(() => ({
		container: {
			display: 'inline-block',
			position: 'relative',
			textAlign: 'center',
			width: 'auto'
		},
		refresh: {
			display: 'inline-block',
			position: 'relative',
			marginRight: '45px',
			marginLeft: '45px'
		}
	}), []);

	const refreshIndicatorDownloadAllButtonLoading = useCallback(() => (
		<div style={loadingStyle.container}>
			<Button className="downloadAll" color="tertiary">
				<RefreshIndicator
					size={'large'}
					left={0}
					top={0}
					status="loading"
					style={loadingStyle.refresh}
				/>
			</Button>
		</div>
	), [loadingStyle]);

	const refreshIndicatorDownloadSelectedButtonLoading = useCallback(() => (
		<div style={loadingStyle.container}>
			<Button alt={true} className="downloadselected" color="primary">
				<RefreshIndicator
					size={'large'}
					left={5}
					top={0}
					status="loading"
					style={loadingStyle.refresh}
				/>
			</Button>
		</div>
	), [loadingStyle]);



	// Recognize revenue handlers
	const [openRevenueModal, setOpenRevenueModal] = useState(false);
	const [showRevenueBanner, setShowRevenueBanner] = useState(false);

	const fetchRecognizeRevenue = useCallback(async () => {
		if (project.projectId) {
			const res = await getRecognizeRevenue(project.projectId);
			if (res && res.payload) {
				if (res.payload.length > 0) {
					//get the latest response if there are multiple
					// Assuming the response is an array and we need the first item
				
				setRecognizeRevenuePayload(res.payload[0]);
				setRecognizeComponentData({
					ButtonText: 'Recognize Revenue completed on ' + (dayjs(res.payload[0].recognizeRevenueDate).format('YYYY-MM-DD hh:mm A') ?? ''),
					TooltipText: `Recognize Revenue was completed on ${dayjs(res.payload[0].recognizeRevenueDate).format('YYYY-MM-DD hh:mm A') ?? ''}.`
				});
			}
			} else {	
				setRecognizeRevenuePayload({
					RecognizeRevenueDate: new Date().toISOString(),
					ProjectId: project.projectId,
					Comment: {
						Comment : '',
						CreatedDate: new Date().toISOString()
					},
					Status: 'Pending'
				});
			}
		}
	}, []);

	// Handler for Recognize Revenue button click
	const handleRecognizeRevenueClick = () => {
		setOpenRevenueModal(true);
	};

	const handleRecognizeRevenueCommentChange = (e) => {
		const comment = e.target.value;
		setRevenueComment({
			Date: new Date().toISOString(),
			Comment: comment
		});
	};

	// Handler for confirming revenue recognition
const handleConfirmRecognizeRevenue = async () => {
    // Format as ISO 8601 with timezone, which is compatible with PostgreSQL timestamptz
    const formattedDate = dayjs().tz('America/New_York').toISOString(); // e.g., "2024-06-19T20:00:00.000-04:00"
    const newPayload = {
        RecognizeRevenueDate: formattedDate,
        ProjectId: project?.projectId,
        Comment: {
            Comment: revenueComment.Comment || "Initial revenue recognition.",
            Date: formattedDate
        },
        Status: "Approved"
    };
    setRecognizeRevenuePayload(newPayload);
    await recognizeRevenue(newPayload);
	await fetchRecognizeRevenue(); // Refresh the revenue data after recognizing revenue
    setOpenRevenueModal(false);
    setShowRevenueBanner(true);
};


	// Handler for closing the modal
	const handleCloseRevenueModal = () => {
		setOpenRevenueModal(false);
	};

	// Handler for closing the banner
	const handleCloseRevenueBanner = () => {
		setShowRevenueBanner(false);
	};


	return (
		<div className="ProjectFiles" data-testid="projectFiles">
			{(visibleFiles) ? (
				<div className="TableView">
					<Stack className="header" direction="row" alignItems="center" justifyContent="space-between">
						<Box className="searchInput" flex={1}>
							<SearchContainer
								onKeyUp={keyUpHandler}
								objectId={project.projectId}
								iconColor={'tertiary'}
								hintText={'Search Files'}
								minSearchChars={3}
								showResults={false}
								dataModel={'files'}
							/>
							{showSearchDialog && <Box>Searching ...</Box>}
							{errormsg && <Box className="errormsg">{errormsg}</Box>}
						</Box>
					</Stack>

					<Stack className="headerActions" direction="row" justifyContent="space-between" alignItems="center">
						{(canEditProjects || isAdmin) && (
							<Box className="endpointSelector" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Switch
									checked={useAdminEndpoint}
									onChange={handleToggleChange}
									size="small"
									color="primary"
								/>
								<Typography variant="body2" color="text.secondary">
									{useAdminEndpoint ? 'Admin' : 'Client'}
								</Typography>
							</Box>
						)}
						
						<Stack spacing={1} direction={{ xs: 'column', md: 'row' }}>
							{(isAdmin && useAdminEndpoint) && <>
								{(!data.isPublishing && selectedStagedFiles.length > 0) && (
									<Button
										color="tertiary"
										alt={selectedStagedFiles.length === 0}
										size="normal"
										onClick={selectedStagedFiles.length > 0 ? handlePublishClick : undefined}
									>
										Publish ({selectedStagedFiles.length}) File{selectedStagedFiles.length > 1 && 's'}
									</Button>
								)}
								{labVantageImportEnabled && (
									<Button
										className="labVantageUploadButton"
										color="tertiary"
										size="normal"
										onClick={() => openLabVantageUploadModal()}
									>
										Upload from Lab Vantage ({labVantageNoticeBadgeCount}) <Icon icon="upload" />
									</Button>
								)}
								<DynamicModalButton
									className="uploadButton"
									onClose={() => { refreshFiles() }}
									color="tertiary"
									modalId={`modal-add-user`}
									modalProps={{ title: "Project files upload" }}
									component={AttachmentUploader}
									componentProps={{
										projectId: project.projectId,
										refreshFiles: refreshFiles,
										projectCode: project.projectCode
									}}>
									Upload Files <Icon icon="upload" />
								</DynamicModalButton>
								{data.isPublishing && refreshIndicatorDownloadSelectedButtonLoading()}
							</>}

							{data.isSelectedLoading ? (
								refreshIndicatorDownloadSelectedButtonLoading()
							) : (
								<Button alt={selectedFileIds.length === 0} size="normal" className="downloadSelected" color="tertiary" onClick={selectedFileIds.length > 0 ? fetchFilesById : undefined}>
									Download Selected{selectedFileIds.length > 0 && `(${selectedFileIds.length})`}
								</Button>
							)}

							{data.isLoading ? (
								refreshIndicatorDownloadAllButtonLoading()
							) : (
								<Button size="normal" className="downloadAll" color="tertiary" onClick={fetchAllFiles}>Download All <Icon icon="download" /></Button>
							)}
						</Stack>
					</Stack>

					<DynamicModal
						open={publishValidationDialogOpen}
						onClose={closePublishValidationDialog}
						title="Double-check files before publishing"
					>
						<DialogContentText>
							Some selected filenames do not include the project code <strong>{project?.projectCode}</strong>.
						</DialogContentText>
						<label>Possible Invalid Files Found:</label>
						<div className="projectCodeGuardCenteredBox">
							<div className="projectCodeGuardFileList">
								{publishValidationMessages.map((message) => (
									<div key={message}>{message}</div>
								))}
							</div>
						</div>
						<DialogContentText>
							Please verify this was intentional before continuing.
						</DialogContentText>
						<div className="projectCodeGuardModalActions">
							<Button onClick={closePublishValidationDialog} color="secondary">
								Cancel
							</Button>
							<Button onClick={confirmPublishWithMismatch} color="primary" autoFocus>
								Publish Anyway
							</Button>
						</div>
					</DynamicModal>

					{labVantageImportEnabled && (
					<DynamicModal
						open={labVantageUploadModalOpen}
						onClose={closeLabVantageUploadModal}
						title="Upload from Lab Vantage"
					>
						{labVantageSummaryLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
								<CircularProgress size={32} />
							</Box>
						) : labVantageAttachments.length === 0 ? (
							<DialogContentText>No Lab Vantage attachments to display.</DialogContentText>
						) : (
							<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" />
											<TableCell>Filename</TableCell>
											<TableCell>Last modified</TableCell>
											<TableCell>Classification</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{labVantageAttachments.map((row, idx) => {
											const classificationNorm = String(row?.classification || '').toLowerCase();
											const isCurrent = classificationNorm === 'current';
											const isEdited = classificationNorm === 'edited';
											const lvFileId = row?.lvFileId ?? '';
											const checked = lvFileId && labVantageImportSelection.includes(lvFileId);
											return (
												<TableRow
													key={lvFileId || `lv-attachment-${idx}`}
													sx={{
														...(isCurrent && {
															opacity: 0.55,
															bgcolor: 'action.hover',
														}),
													}}
												>
													<TableCell padding="checkbox">
														<Checkbox
															size="small"
															disabled={isCurrent || !lvFileId}
															checked={checked}
															onChange={() => toggleLabVantageImportSelection(lvFileId, classificationNorm)}
															inputProps={{
																'aria-label': `Select ${row?.fileName || 'file'} for import`,
															}}
														/>
													</TableCell>
													<TableCell>
														<Stack direction="row" alignItems="center" spacing={0.5}>
															<Typography variant="body2" component="span">
																{row?.fileName ?? '--'}
															</Typography>
															{isEdited && (
																<Tooltip title={LAB_VANTAGE_EDITED_TOOLTIP}>
																	<WarningAmber
																		fontSize="small"
																		color="warning"
																		sx={{ verticalAlign: 'middle' }}
																		aria-label={LAB_VANTAGE_EDITED_TOOLTIP}
																	/>
																</Tooltip>
															)}
														</Stack>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{formatLabVantageModifiedAt(row?.lastModifiedAt)}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{classificationNorm || '--'}
														</Typography>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							flexWrap="wrap"
							gap={1}
							sx={{ mt: 2 }}
							className="projectCodeGuardModalActions"
						>
							<Button
								color="tertiary"
								onClick={clearLabVantageNotice}
								disabled={labVantageImportSubmitting || labVantageAcknowledgeSubmitting}
							>
								{labVantageAcknowledgeSubmitting ? (
									<>
										<CircularProgress size={18} sx={{ mr: 1, verticalAlign: 'middle' }} color="inherit" />
										Clearing...
									</>
								) : (
									'Clear Lab Vantage Notice'
								)}
							</Button>
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
								<Button
									onClick={closeLabVantageUploadModal}
									color="tertiary"
									disabled={labVantageImportSubmitting || labVantageAcknowledgeSubmitting}
								>
									Close
								</Button>
								<Button
									color="primary"
									onClick={submitLabVantageImport}
									disabled={
										labVantageImportSubmitting ||
										labVantageAcknowledgeSubmitting ||
										labVantageImportSelection.length === 0
									}
								>
									{labVantageImportSubmitting ? (
										<>
											<CircularProgress size={18} sx={{ mr: 1, verticalAlign: 'middle' }} color="inherit" />
											Importing...
										</>
									) : (
										`Import selected (${labVantageImportSelection.length})`
									)}
								</Button>
							</Stack>
						</Stack>
					</DynamicModal>
					)}

					<Dialog
						open={openRevenueModal}
						onClose={handleCloseRevenueModal}
					>
						<DialogTitle>Confirm Revenue Recognition</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Are you sure you want to recognize revenue for this project? This action cannot be undone.
							</DialogContentText>
							<TextField
								autoFocus
								margin="dense"
								id="revenue-comment"
								label="Comment"
								type="text"
								fullWidth
								variant="standard"
								value={revenueComment.Comment}
								onChange={handleRecognizeRevenueCommentChange}
								placeholder="Add a comment (optional)"
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseRevenueModal} color="secondary">
								Cancel
							</Button>
							<Button onClick={handleConfirmRecognizeRevenue} color="primary" autoFocus>
								Confirm
							</Button>
						</DialogActions>
					</Dialog>

					<Snackbar
						open={showRevenueBanner}
						autoHideDuration={4000}
						onClose={handleCloseRevenueBanner}
						anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
					>
						<Alert onClose={handleCloseRevenueBanner} severity="success" sx={{ width: '100%' }}>
							Revenue recognized successfully!
						</Alert>
					</Snackbar>

					{showFiles ? (
						<FilesTable
							project={project}
							projectId={project.projectId}
							attachmentIsLoading={attachmentIsLoading}
							handleAttachmentUpload={undefined}
							onRemove={useAdminEndpoint ? onRemoveFile : undefined}
							updateFileChanges={updateFileChanges}
							onSaveAdminFilesClick={undefined}
							isSavingFiles={isSavingFiles}
							viewName={"PageEvent_Results"}
							files={visibleFiles}
							onCheckboxClick={checkboxClickHandler}
							selections={selectedFileIds}
							useAdminEndpoint={useAdminEndpoint}
							updateFilePropertiesAdmin={updateFilePropertiesAdmin}
							refreshFiles={refreshFiles}
							updateFileInVisibleFiles={updateFileInVisibleFiles}
							showLabVantageEditedInFilesTable={shouldFetchLabVantageSummary}
							labVantageEditedPortalFileIds={labVantageEditedPortalFileIds}
							onOpenLabVantageUploadModal={openLabVantageUploadModal}
						/>
					) : (
						<LoadingSpinner />
					)}
				</div>) : (
				<EmptyState />
			)
			}
		</div>
	);
};

ProjectFiles.propTypes = {
	project: PropTypes.object.isRequired,
	publishPreviewMode: PropTypes.bool,
	projectId: PropTypes.string
};

export default ProjectFiles;

